// "2개종목비교" 팝업 (건별수익률현황 종목별 탭 - 차수 클릭 시 오픈)
// 국내ETF vs 비교대상(종목/지수) vs 환율, 정규화(시작점=100) 3라인 그래프
// 그래프 렌더링 로직은 시장지수현황(MarketIndexPage) '2개종목비교' 탭과 공유 (CompareChart.js)
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchMultiPriceHistory } from '../../api/price';
import { fetchMarketIndex } from '../../api/marketIndex';
import {
  COMPARE_COLORS, normalizeSeries, marketFieldToSeries, mergeNormalized,
  totalChangeRate, CompareLineChart,
} from './CompareChart';
import './StockCompareModal.css';

const toYMD = (d) => d.toISOString().slice(0, 10);
const today = () => toYMD(new Date());
const calcDateRange = (days) => {
  const end = new Date(), start = new Date();
  start.setDate(end.getDate() - days);
  return { start: toYMD(start), end: toYMD(end) };
};

const PERIODS = [
  { key: '3m', label: '3개월', days: 90   },
  { key: '6m', label: '6개월', days: 180  },
  { key: '1y', label: '1년',   days: 365  },
  { key: '2y', label: '2년',   days: 730  },
  { key: '3y', label: '3년',   days: 1095 },
  { key: '5y', label: '5년',   days: 1825 },
  { key: 'custom', label: '기간선택', days: null },
];

const INDEX_LABELS = {
  kospi: '코스피', kosdaq: '코스닥', sp500: 'S&P500', nasdaq100: '나스닥100', gold: '금',
};

const sign = (v) => v > 0 ? '+' : '';
const fPct = (v) => v == null ? '-' : `${sign(v)}${v.toFixed(1)}%`;
const fPp  = (v) => v == null ? '-' : `${sign(v)}${v.toFixed(1)}%p`;

const StockCompareModal = ({
  ticker,
  stockName,
  compareType,
  compareTicker,
  compareIndexField,
  defaultFrom,   // 해당 매수건의 buy_date — 팝업 오픈 시 기간선택 시작일 기본값
  onClose,
}) => {
  const [periodKey,   setPeriodKey]   = useState('custom');
  const [customStart, setCustomStart] = useState(defaultFrom || calcDateRange(365).start);
  const [customEnd,   setCustomEnd]   = useState(today());
  const [dateRange,   setDateRange]   = useState({ start: defaultFrom || calcDateRange(365).start, end: today() });
  const [domesticHist, setDomesticHist] = useState([]);
  const [compareHist,  setCompareHist]  = useState([]);
  const [marketData,   setMarketData]   = useState([]);
  const [loading,      setLoading]      = useState(false);
  const overlayRef = useRef(null);

  const hasCompare = compareType === 'STOCK' ? !!compareTicker
    : compareType === 'INDEX' ? !!compareIndexField
    : false;

  const compareLabel = compareType === 'STOCK'
    ? compareTicker
    : (INDEX_LABELS[compareIndexField] || compareIndexField);

  // ESC 닫기
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 기간 버튼 선택 시 날짜범위 재계산 (기간선택은 사용자가 직접 입력)
  useEffect(() => {
    if (periodKey === 'custom') return;
    const p = PERIODS.find(x => x.key === periodKey);
    if (p) setDateRange(calcDateRange(p.days));
  }, [periodKey]);

  const handleCustomApply = () => {
    if (!customStart || !customEnd) return;
    setDateRange({ start: customStart, end: customEnd });
  };

  // 데이터 로드
  useEffect(() => {
    if (!hasCompare || !dateRange.start || !dateRange.end) return;
    let cancelled = false;
    setLoading(true);
    const tickers = compareType === 'STOCK' ? [ticker, compareTicker] : [ticker];

    Promise.all([
      fetchMultiPriceHistory(tickers, dateRange.start, dateRange.end),
      fetchMarketIndex(dateRange.start, dateRange.end),
    ]).then(([histRes, marketRes]) => {
      if (cancelled) return;
      const histData = histRes?.data || {};
      setDomesticHist(histData[ticker] || []);
      setCompareHist(compareType === 'STOCK' ? (histData[compareTicker] || []) : []);
      setMarketData(marketRes?.data || []);
    }).catch(() => {
      if (!cancelled) { setDomesticHist([]); setCompareHist([]); setMarketData([]); }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [hasCompare, ticker, compareType, compareTicker, dateRange]);

  const { merged, series, summary } = useMemo(() => {
    const domesticNorm = normalizeSeries(domesticHist);
    const compareNorm  = compareType === 'STOCK'
      ? normalizeSeries(compareHist)
      : normalizeSeries(marketFieldToSeries(marketData, compareIndexField));
    const fxNorm = normalizeSeries(marketFieldToSeries(marketData, 'usd_krw'));

    const mergedData = mergeNormalized({ domestic: domesticNorm, compare: compareNorm, fx: fxNorm });
    const seriesDef = [
      { key: 'domestic', label: `${stockName || ticker}`, color: COMPARE_COLORS.domestic },
      { key: 'compare',  label: compareLabel,             color: COMPARE_COLORS.compare  },
      { key: 'fx',       label: '원달러환율',              color: COMPARE_COLORS.fx, axis: 'right' },
    ];
    const summaryInfo = {
      domestic: totalChangeRate(domesticNorm),
      compare:  totalChangeRate(compareNorm),
      fx:       totalChangeRate(fxNorm),
    };
    summaryInfo.diff = (summaryInfo.domestic != null && summaryInfo.compare != null)
      ? Math.round((summaryInfo.domestic - summaryInfo.compare) * 10) / 10
      : null;
    return { merged: mergedData, series: seriesDef, summary: summaryInfo };
  }, [domesticHist, compareHist, marketData, compareType, compareIndexField, stockName, ticker, compareLabel]);

  const handleOverlayClick = e => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div className="scm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="scm-panel">
        <div className="scm-header">
          <div className="scm-title">
            2개종목비교
            <span className="scm-title-sub">{stockName || ticker} ({ticker}) vs {hasCompare ? compareLabel : '비교대상 미설정'}</span>
          </div>
          <button className="scm-close" onClick={onClose}>✕</button>
        </div>

        <div className="scm-period-bar">
          {PERIODS.map(p => (
            <button
              key={p.key}
              className={`scm-period-btn${periodKey === p.key ? ' active' : ''}`}
              onClick={() => setPeriodKey(p.key)}
            >
              {p.label}
            </button>
          ))}
          {periodKey === 'custom' && (
            <span className="scm-custom-range">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
              <span>~</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
              <button className="scm-apply-btn" onClick={handleCustomApply}>조회</button>
            </span>
          )}
        </div>

        <div className="scm-body">
          {!hasCompare ? (
            <div className="scm-empty">
              이 종목에는 비교대상이 설정되어 있지 않습니다.<br />
              종목관리 화면에서 비교대상(종목 또는 지수)을 먼저 등록해주세요.
            </div>
          ) : loading ? (
            <div className="scm-empty">조회 중...</div>
          ) : merged.length === 0 ? (
            <div className="scm-empty">해당 기간에 표시할 시세 데이터가 없습니다.</div>
          ) : (
            <>
              <CompareLineChart
                title={`${stockName || ticker} vs ${compareLabel}`}
                desc="조회기간 시작 시점 = 100 기준 정규화"
                mergedData={merged}
                series={series}
                height={640}
              />
              <div className="scm-summary">
                <span>{stockName || ticker} <strong style={{ color: COMPARE_COLORS.domestic }}>{fPct(summary.domestic)}</strong></span>
                <span className="scm-sep">/</span>
                <span>{compareLabel} <strong style={{ color: COMPARE_COLORS.compare }}>{fPct(summary.compare)}</strong></span>
                <span className="scm-sep">/</span>
                <span>환율 <strong style={{ color: COMPARE_COLORS.fx }}>{fPct(summary.fx)}</strong></span>
                <span className="scm-sep">/</span>
                <span>
                  {stockName || ticker}-{compareLabel} 차이&nbsp;
                  <strong style={{ color: summary.diff > 0 ? '#c0392b' : summary.diff < 0 ? '#1a6eb5' : undefined }}>
                    {fPp(summary.diff)}
                  </strong>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockCompareModal;
